import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { isOwner, lastSeen } from "../pages/Account";
import { toast } from "sonner";
import { Megaphone, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Panel, Empty } from "./Widgets";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

export function MessageManager({ agentId, title }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const load = () => api.get("/messages", { params: agentId ? { agent_id: agentId } : {} })
    .then((r) => setItems(agentId ? r.data : r.data.filter((m) => !m.agent_id)));
  useEffect(() => { load(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const post = async () => {
    try {
      await api.post("/messages", { agent_id: agentId || null, text });
      setText(""); toast.success("Message posted to portal"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/messages/${id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Panel title={title} className="mt-6" action={<Megaphone size={15} className="text-slate-500" />}>
      <div className="p-5 space-y-3" data-testid={agentId ? "agent-message-manager" : "broadcast-message-manager"}>
        <Textarea data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
          placeholder={agentId ? "Shown only on this agent's portal…" : "Shown on every agent's portal…"} className="bg-[#0B0F17] min-h-[60px]" />
        <div className="flex justify-end"><Button data-testid="post-message-button" size="sm" disabled={!text.trim()} onClick={post} className="bg-blue-500 hover:bg-blue-600 text-white">Post message</Button></div>
        {items.length === 0 ? <Empty>No active messages.</Empty> : items.map((m) => (
          <div key={m.id} className="flex justify-between gap-3 rounded-md border border-slate-800 bg-[#0B0F17] p-3" data-testid={`message-${m.id}`}>
            <div><p className="text-sm whitespace-pre-wrap">{m.text}</p><div className="text-[11px] font-mono text-slate-500 mt-1">{new Date(m.created_at).toLocaleString()}</div></div>
            {isOwner(user) && <button data-testid={`delete-message-${m.id}`} onClick={() => remove(m.id)} className="text-slate-600 hover:text-rose-400 shrink-0"><Trash2 size={14} /></button>}
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function PortalMessages() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/my-messages").then((r) => setItems(r.data)); }, []);
  if (!items.length) return null;
  return (
    <div className="space-y-2 mb-6" data-testid="portal-messages">
      {items.map((m) => (
        <div key={m.id} className="fade-up flex gap-3 items-start rounded-md border border-blue-500/30 bg-blue-500/10 px-4 py-3" data-testid={`portal-message-${m.id}`}>
          <Megaphone size={16} className="text-blue-300 mt-0.5 shrink-0" />
          <div><p className="text-sm text-slate-100 whitespace-pre-wrap">{m.text}</p>
            <div className="text-[11px] font-mono text-slate-400 mt-1">{new Date(m.created_at).toLocaleDateString()} · {m.agent_id ? "for you" : "for all agents"}</div></div>
        </div>
      ))}
    </div>
  );
}
